import type { FormKitNode } from '@formkit/core'
import type { Ref } from 'vue'
import { getError } from '@autobid/strapi-integration/utils/getError'
import {
  BID_AGENT_FORM_KEY_DESKTOP,
  BID_AGENT_FORM_KEY_MOBILE,
  BID_FORM_KEY_DESKTOP,
  BID_FORM_KEY_MOBILE,
} from '@autobid/ui/constants/BAROMETER_FORMS_IDS'
import { formatPrice } from '@autobid/ui/utils/formatPrice'
import { reset } from '@formkit/core'
import { useMutation } from '@tanstack/vue-query'
import { computed } from 'vue'
import { getMinimalBidStep } from '../../utils/car/bidValidation'
import { useSkipGlobalErrorBoundary } from '../useSkipGlobalErrorBoundary'

/**
 * 1 - normal bid, 2 - agent bid
 */
type Type = 1 | 2

export interface Bid {
  currentValue: number
  currentPrice: number
  currentType: Type
  currentId: number
}

interface SubmitParams {
  currentType: Type
  currentPrice: number
  currentId: number
  skipConfirmation?: boolean
}

interface MutationParams {
  lang: string
  id: number
  type: Type
  price: number
  tte?: number
  currentPrice?: number
}

const TRANSITION_DELAY = 300

interface Options {
  bidOptions: Ref<number[]>
  disableToasts?:
    | {
      success?: boolean
      error?: boolean
      warning?: boolean
    }
    | boolean
  skipReset?: boolean
}

function checkShowToast(
  type: 'success' | 'error' | 'warning',
  options: Options,
) {
  if (options.disableToasts === true) {
    return false
  }
  if (typeof options.disableToasts === 'object') {
    return !options.disableToasts[type]
  }
  return true
}

export function useBidAction(options: Options) {
  useSkipGlobalErrorBoundary(true)
  const push = usePush()
  const inputRef = ref<any | null>()
  const agentInputRef = ref<any | null>()
  const temporaryBid = ref<number | null>(null)
  const type = ref<Type>(1)
  const isConfirmationOpen = ref(false)
  const temporaryCurrentPrice = ref(0)
  const id = ref(0)
  const { locale, t } = useI18n()
  const { $trpc } = useNuxtApp()

  const mutationFn = async ({
    id,
    type,
    price,
    tte,
    currentPrice,
  }: MutationParams) => {
    const result = await $trpc.auctions.makeBid.mutate({
      carId: id,
      currentPrice,
      tte,
      price,
      type,
    })

    return {
      ...result.data,
      type,
    }
  }

  const { mutateAsync, isLoading: isBidding } = useMutation({
    mutationFn,
    onError: (error) => {
      const { message, code, handled } = getError(error)

      if (!handled) {
        if (code === 401) {
          isConfirmationOpen.value = false
          return
        }

        if (checkShowToast('error', options)) {
          push.error(message)
        }
      }
    },
    onSuccess: (result) => {
      const resultLevel = result.level.toLowerCase() as Lowercase<
        typeof result.level
      >

      const shouldDisplayToast = checkShowToast(resultLevel, options)
      if (!shouldDisplayToast) {
        return
      }
      push[resultLevel]({
        message: result.message,
        props: {
          internalId: result.type === 1 ? 'bid' : 'setAgent',
        },
      })
    },
  })

  const clearAfterTransition = () => {
    setTimeout(() => {
      if (isConfirmationOpen.value)
        return

      temporaryBid.value = null
      temporaryCurrentPrice.value = 0
      id.value = 0
    }, TRANSITION_DELAY)
  }

  const handleBid = ({
    currentId,
    currentValue,
    currentPrice,
    currentType,
  }: Bid) => {
    // if temporary bid already exists, it means that user fill the form
    temporaryBid.value = temporaryBid.value ?? currentValue + currentPrice
    isConfirmationOpen.value = true
    type.value = currentType
    temporaryCurrentPrice.value = currentPrice
    id.value = currentId
  }

  const handleBidConfirmation = async () => {
    if (!isConfirmationOpen.value) {
      return
    }

    if (!options.skipReset) {
      reset(BID_AGENT_FORM_KEY_MOBILE)
      reset(BID_AGENT_FORM_KEY_DESKTOP)
      reset(BID_FORM_KEY_DESKTOP)
      reset(BID_FORM_KEY_MOBILE)
    }

    await mutateAsync({
      price: Number(temporaryBid.value),
      lang: locale.value,
      type: type.value,
      currentPrice: temporaryCurrentPrice.value,
      id: id.value,
    })
    isConfirmationOpen.value = false
    clearAfterTransition()
  }

  const handleSubmit = ({
    currentType,
    currentPrice,
    currentId,
    skipConfirmation,
  }: SubmitParams) => {
    const value
      = currentType === 1
        ? inputRef.value?.node.value
        : agentInputRef.value?.node.value

    if (!value)
      return

    temporaryBid.value = +value
    handleBid({
      currentValue: +value,
      currentPrice,
      currentType,
      currentId,
    })
    if (skipConfirmation) {
      handleBidConfirmation()
    }
  }

  const handleBidCancel = () => {
    isConfirmationOpen.value = false
    clearAfterTransition()
  }

  interface ValidationProps {
    node: FormKitNode
    name: string
    args: any[]
  }

  const minPriceValidationMessage = ({ name, args }: ValidationProps) => {
    return t('barometer.bid-min', {
      field: name,
      minPrice: formatPrice(args[0]),
    })
  }
  const bidStepPriceValidationMessage = ({ name, args }: ValidationProps) => {
    return t('barometer.bid-validation', {
      field: name,
      bidStep: formatPrice(args[0]),
      minPrice: formatPrice(args[1]),
    })
  }

  const inputBidStep = computed(() =>
    getMinimalBidStep(options.bidOptions.value),
  )

  return {
    handleSubmit,
    handleBid,
    handleBidConfirmation,
    inputRef,
    isConfirmationOpen,
    handleBidCancel,
    agentInputRef,
    type,
    isBidding,
    minPriceValidationMessage,
    bidStepPriceValidationMessage,
    inputBidStep,
    temporaryBid: readonly(temporaryBid),
  }
}
